import { FileText, HelpCircle, MessageCircle, Minimize2, Send, Shield, Sparkles, Ticket, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'
import { getTemplateById, searchTemplates } from '../../data/supportTemplates'
import BotAnimation from '../landing/BotAnimation'
import './SupportWidget.css'

type WidgetTab = 'chat' | 'about' | 'legal' | 'support'

interface Message {
  id: string
  type: 'user' | 'bot'
  content: string
  timestamp: Date
  quickActions?: Array<{ label: string; templateId: string }>
}

export default function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [tab, setTab] = useState<WidgetTab>('chat')
  const [legalSub, setLegalSub] = useState<'terms' | 'privacy'>('terms')
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [unreadCount, setUnreadCount] = useState(0)
  const [botMode, setBotMode] = useState<'happy' | 'sad' | 'surprised'>('happy')
  const [guestName, setGuestName] = useState(() => localStorage.getItem('guest_name') || '')
  const [guestEmail, setGuestEmail] = useState(() => localStorage.getItem('guest_email') || '')
  const [guestCollected, setGuestCollected] = useState(() => !!localStorage.getItem('guest_email'))
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const getWelcomeQuickActions = useCallback(() => [
    { label: 'What is RunAI?', templateId: 'what-is-runai' },
    { label: 'How to start?', templateId: 'how-to-start' },
    { label: 'What is Agent?', templateId: 'what-is-agent' },
    { label: 'What is AiCloud?', templateId: 'what-is-aicloud' },
  ], [])

  const addBotMessage = useCallback((content: string, quickActions?: Array<{ label: string; templateId: string }>) => {
    setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, type: 'bot', content, timestamp: new Date(), quickActions }])
  }, [])

  const addUserMessage = useCallback((content: string) => {
    setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random()}`, type: 'user', content, timestamp: new Date() }])
  }, [])

  useEffect(() => {
    const saved = localStorage.getItem('support_messages')
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Array<{ id: string; type: string; content: string; timestamp: string; quickActions?: Array<{ label: string; templateId: string }> }>
        setMessages(parsed.map(m => ({ ...m, type: m.type as 'user' | 'bot', timestamp: new Date(m.timestamp) })))
      } catch { /* */ }
    } else {
      addBotMessage('Hi! How can I help you with RunAI?\n\nSelect a topic below or ask directly.', getWelcomeQuickActions())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { if (messages.length > 0) localStorage.setItem('support_messages', JSON.stringify(messages)) }, [messages])
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])
  useEffect(() => {
    if (!isOpen && messages.length > 0 && messages[messages.length - 1].type === 'bot') setUnreadCount(prev => prev + 1)
    else setUnreadCount(0)
  }, [messages, isOpen])
  useEffect(() => {
    const interval = setInterval(() => setBotMode(prev => prev === 'happy' ? 'sad' : prev === 'sad' ? 'surprised' : 'happy'), 6000)
    return () => clearInterval(interval)
  }, [])

  const handleQuickAction = (templateId: string) => {
    const template = getTemplateById(templateId)
    if (!template) return
    addUserMessage(template.question)
    setTimeout(() => addBotMessage(template.answer, template.quickActions), 500)
  }

  const handleSend = () => {
    if (!inputValue.trim()) return
    const text = inputValue.trim()
    addUserMessage(text)
    setInputValue('')
    void (async () => {
      const localMatches = searchTemplates(text)
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('access_token') || ''
        const res = await apiFetch(`${API_URL}/api/llm/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ messages: [{ role: 'user', content: text }], agent_id: 522, user_id: 0 }),
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        const answer = data?.choices?.[0]?.message?.content ?? data?.answer ?? data?.response ?? ''
        if (answer) addBotMessage(answer)
        else if (localMatches.length > 0) addBotMessage(localMatches[0].answer, localMatches[0].quickActions)
        else addBotMessage('Unable to connect. Please try again later.', getWelcomeQuickActions())
      } catch {
        if (localMatches.length > 0) addBotMessage(localMatches[0].answer, localMatches[0].quickActions)
        else addBotMessage('Unable to connect. Please try again later.', getWelcomeQuickActions())
      }
    })()
  }

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} className={`support-widget-button bot-${botMode}`}>
        <div className="widget-bot-wrapper"><BotAnimation mode={botMode} /></div>
        {unreadCount > 0 && <div className="unread-badge">{unreadCount}</div>}
      </button>
    )
  }

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, width: 380, height: isMinimized ? 60 : 600, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', zIndex: 1000, overflow: 'hidden', transition: 'height 0.3s' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: isMinimized ? 'pointer' : 'default', flexShrink: 0 }}
        onClick={() => { if (isMinimized) setIsMinimized(false) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MessageCircle size={18} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>RunAI Help</div>
            <div style={{ fontSize: 10, opacity: 0.9 }}>AI assistant · Support · About · Legal</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={e => { e.stopPropagation(); setIsMinimized(!isMinimized) }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><Minimize2 size={14} /></button>
          <button onClick={e => { e.stopPropagation(); setIsOpen(false) }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}><X size={14} /></button>
        </div>
      </div>

      {!isMinimized && (<>
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          {([
            { id: 'chat' as WidgetTab, label: 'Ask AI', icon: <Sparkles size={11} /> },
            { id: 'about' as WidgetTab, label: 'About', icon: <HelpCircle size={11} /> },
            { id: 'legal' as WidgetTab, label: 'Legal', icon: <Shield size={11} /> },
            { id: 'support' as WidgetTab, label: 'Support', icon: <Ticket size={11} /> },
          ]).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              padding: '8px 4px', fontSize: 10, fontWeight: tab === t.id ? 700 : 400,
              color: tab === t.id ? '#667eea' : '#6b7280', background: 'transparent', border: 'none',
              borderBottom: tab === t.id ? '2px solid #667eea' : '2px solid transparent', cursor: 'pointer',
            }}>{t.icon} {t.label}</button>
          ))}
        </div>

        {/* ── CHAT TAB ── */}
        {tab === 'chat' && (<>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map(msg => (
              <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.type === 'user' ? 'flex-end' : 'flex-start', gap: 6 }}>
                <div style={{
                  maxWidth: '80%', padding: '10px 14px',
                  borderRadius: msg.type === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: msg.type === 'user' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f8fafc',
                  color: msg.type === 'user' ? '#fff' : '#1a1d29', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                }}>{msg.content}</div>
                {msg.quickActions && msg.quickActions.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: '80%' }}>
                    {msg.quickActions.map((action, idx) => (
                      <button key={idx} onClick={() => handleQuickAction(action.templateId)} style={{
                        padding: '6px 12px', borderRadius: 16, background: '#fff', border: '1px solid #e2e8f0',
                        color: '#667eea', fontSize: 11, fontWeight: 500, cursor: 'pointer',
                      }}>{action.label}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, flexShrink: 0, flexDirection: 'column' }}>
            {!guestCollected && !localStorage.getItem('token') ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Enter your info to chat with AI:</div>
                <input type="text" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Your name"
                  style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, outline: 'none' }} />
                <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Your email"
                  style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, outline: 'none' }} />
                <button onClick={() => {
                  if (guestName.trim() && guestEmail.trim() && guestEmail.includes('@')) {
                    localStorage.setItem('guest_name', guestName.trim())
                    localStorage.setItem('guest_email', guestEmail.trim())
                    setGuestCollected(true)
                    addBotMessage(`Thanks ${guestName.trim()}! You can now ask me anything about RunAI.`, getWelcomeQuickActions())
                  }
                }} disabled={!guestName.trim() || !guestEmail.trim() || !guestEmail.includes('@')} style={{
                  padding: '8px 14px', borderRadius: 8,
                  background: guestName.trim() && guestEmail.includes('@') ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e2e8f0',
                  border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: guestName.trim() && guestEmail.includes('@') ? 'pointer' : 'not-allowed',
                }}>Start Chatting</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSend() }}
                  placeholder="Ask your question..."
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
                <button onClick={handleSend} disabled={!inputValue.trim()} style={{
                  padding: '8px 14px', borderRadius: 8,
                  background: inputValue.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#e2e8f0',
                  border: 'none', color: '#fff', cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center',
                }}><Send size={14} /></button>
              </div>
            )}
          </div>
        </>)}

        {/* ── ABOUT TAB ── */}
        {tab === 'about' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, fontSize: 13, color: '#4b5563', lineHeight: 1.7 }}>
            {[
              { title: 'What is RunAI?', text: 'RunAI.live is an AI + Human collaboration platform for research, work, and social. 521 AI agents, 6 cloud platforms, FLZ token economy.' },
              { title: 'Vision', text: 'A transparent, efficient marketplace where AI capabilities are accessible to everyone.' },
              { title: 'Mission', text: 'Democratize AI-powered research tools through a fair token-based economy with EQ System emotional intelligence.' },
              { title: 'The Filiz Family', text: 'Mother Filiz (EQ Manager), Father Filiz (Project Manager), 18 Expert Agents, 500 Baby Filiz agents, and AskAI (Agent #522).' },
              { title: 'Services', text: '🔬 Research — Scientific projects with AI agents\n💼 Work — Freelance marketplace with AI\n💬 Social — Human & AI collaboration feed\n☁️ AiCloud — Your own cloud infrastructure\n🪙 FLZ Tokens — Platform currency' },
              { title: 'Tech Stack', text: 'Rust/Axum · React · PostgreSQL 17 · Cloudflare Workers AI · Qdrant Vector DB · LlamaFactory' },
            ].map(s => (
              <div key={s.title} style={{ padding: 12, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 10 }}>
                <strong style={{ color: '#667eea', fontSize: 12 }}>{s.title}</strong>
                <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: 12 }}>{s.text}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── LEGAL TAB ── */}
        {tab === 'legal' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {([
                { id: 'terms' as const, label: 'Terms', icon: <FileText size={10} /> },
                { id: 'privacy' as const, label: 'Privacy', icon: <Shield size={10} /> },
              ]).map(s => (
                <button key={s.id} onClick={() => setLegalSub(s.id)} style={{
                  flex: 1, padding: 8, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: legalSub === s.id ? '#667eea' : '#f3f4f6', color: legalSub === s.id ? '#fff' : '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}>{s.icon} {s.label}</button>
              ))}
            </div>
            <p style={{ fontSize: 10, color: '#9ca3af', marginBottom: 10 }}>Last updated: February 2026</p>
            {(legalSub === 'terms' ? [
              { t: '1. Acceptance', c: 'By using RunAI, you agree to these terms.' },
              { t: '2. Accounts', c: 'One account per person. Keep credentials secure. Must be 18+.' },
              { t: '3. Content', c: 'You own your content. Grant RunAI license to display.' },
              { t: '4. AI Agents', c: 'Tools, not humans. No accuracy guarantee.' },
              { t: '5. FLZ Tokens', c: 'Platform currency. No refunds. Prices may change.' },
              { t: '6. Prohibited', c: 'No illegal activity, harassment, spam, malware.' },
              { t: '7. Termination', c: 'We may terminate violating accounts.' },
              { t: '8. Liability', c: 'Provided "as is" without warranties.' },
            ] : [
              { t: '1. Data Collection', c: 'Account info, usage data, anonymous analytics.' },
              { t: '2. Usage', c: 'Provide services, improve platform, communicate.' },
              { t: '3. Sharing', c: 'Never sell data. Cloud providers get minimal data.' },
              { t: '4. AiCloud', c: 'User Cloud: ALL data in YOUR account. Zero-knowledge.' },
              { t: '5. Cookies', c: 'Essential only. No third-party tracking.' },
              { t: '6. Security', c: 'TLS 1.3, AES-256 encryption.' },
              { t: '7. Rights', c: 'Access, correct, export, delete your data anytime.' },
              { t: '8. Contact', c: 'privacy@runai.live' },
            ]).map(s => (
              <div key={s.t} style={{ padding: 10, background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 8 }}>
                <strong style={{ color: '#667eea', fontSize: 11 }}>{s.t}</strong>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#4b5563' }}>{s.c}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── SUPPORT TAB ── */}
        {tab === 'support' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <div style={{ padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 12 }}>
              <strong style={{ color: '#667eea', fontSize: 13 }}>Need Help?</strong>
              <p style={{ margin: '6px 0 10px', fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                Switch to Ask AI for instant answers, or visit the full Help Center for ticket support.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setTab('chat')} style={{ padding: '8px 14px', borderRadius: 8, background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Ask AI</button>
                <a href="/ask-ai" style={{ padding: '8px 14px', borderRadius: 8, background: '#fff', border: '1px solid #667eea', color: '#667eea', fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>Full Help Center</a>
              </div>
            </div>
            <div style={{ padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 12 }}>
              <strong style={{ color: '#667eea', fontSize: 13 }}>Quick Info</strong>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                {[
                  { label: 'Response', value: '< 24h', color: '#10b981' },
                  { label: 'Categories', value: 'Bug, Feature, Q&A', color: '#667eea' },
                  { label: 'Priority', value: 'Low / Med / High', color: '#f59e0b' },
                  { label: 'Tracking', value: 'Open → Resolved', color: '#8b5cf6' },
                ].map(s => (
                  <div key={s.label} style={{ padding: 8, background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 9, color: '#9ca3af', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
              <strong style={{ color: '#667eea', fontSize: 13 }}>Contact</strong>
              {[
                { label: 'Email', value: 'support@runai.live' },
                { label: 'Discord', value: 'discord.gg/runai' },
                { label: 'Twitter', value: '@runai_live' },
              ].map(c => (
                <div key={c.label} style={{ display: 'flex', gap: 10, fontSize: 12, marginTop: 6 }}>
                  <span style={{ color: '#9ca3af', width: 55 }}>{c.label}</span>
                  <span style={{ color: '#667eea', fontWeight: 600 }}>{c.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>)}
    </div>
  )
}
